import 'whatwg-fetch';
import cookie from 'react-cookie';
import { push } from 'utils/navigation';
import SessionActions from '../actions/SessionActions';
import publisher from './publisher';
import advertiser from './advertiser';
import publisherPaymentDetails from './publisherPaymentDetails';
import publisherPostbacks from './publisherPostbacks';
import publisherBanners from './publisherBanners';
import publisherLandings from './publisherLandings';
import publisherContainrs from './publisherContainrs';
import PaceProgressActions from '../actions/PaceProgressActions';
import user from './user';
import config from '../config.js';
import lct from '../helpers/location';

const cpaUrl = config.cpaUrl;
const apiBaseUrl = `${cpaUrl}/api`;
const authUrl = cpaUrl;
const clientId = config.clientId;

const formUrl = (path) => {
    if (!path) return cpaUrl;
    let p = String(path);
    p = p.replace(/^undefined/, '');
    if (/^https?:\/\//i.test(p)) return p;
    if (!p.startsWith('/')) p = `/${p}`;
    if (/^\/(user|publisher|advertiser|statistic|report|postback)\b/.test(p)) {
        return `${apiBaseUrl}${p}`;
    }
    if (p.startsWith('/api/')) {
        return `${apiBaseUrl}${p.replace(/^\/api/, '')}`;
    }
    return `${cpaUrl}${p}`;
};

const formData = (data) => {
    let formData = new FormData();

    for (let key in data) {
        if (data[key]) {
            formData.append(key, data[key]);
        }
    }

    return formData;
};

export default {
    ...publisher,
    ...advertiser,
    ...publisherPaymentDetails,
    ...publisherPostbacks,
    ...publisherBanners,
    ...publisherLandings,
    ...publisherContainrs,
    ...user,

    getAccessToken() {
        const location = window.location;
        const baseUrl = `${location.origin}${location.pathname}${location.search}`;
        window.location.assign(`${authUrl}/user/get_access_token?client_id=${clientId}&redirect_uri=${encodeURIComponent(baseUrl)}`);
    },

    updateRefreshToken(params) {
        const url = `${authUrl}/user/refresh_token`;
        
        return fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: config.corsCredentials ? 'include' : 'omit',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(params).toString()
        }).then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        });
    },

    makeRequest(path, params = {}) {
        SessionActions.userAuthentication();

        const rawPath = String(path || '');
        // For .get endpoints, always use GET method
        const defaultMethod = (/\.get(?:$|\?|#)/i.test(rawPath) ? 'GET' : 'POST');
        const queryMethod = params.queryMethod || defaultMethod;
        let accessData = {};
        try {
            const cookieData = cookie.load(config.nameCookie);
            if (cookieData && typeof cookieData === 'object') accessData = cookieData;
        } catch (e) { accessData = {}; }
        const accessToken = accessData.access_token;

        const useCredentials = !!config.corsCredentials;

        const baseParams = typeof params === 'string' ? { fields: params } : { ...(params || {}) };
        const shouldRemoveLocale = !!baseParams.removeLocale;
        const locale = shouldRemoveLocale ? undefined : 'en';
        delete baseParams.removeLocale;
        delete baseParams.queryMethod;
        const asJson = !!baseParams.asJson;
        delete baseParams.asJson;

        const tryFetch = (withAuthHeader = true) => {
            let localUrl = formUrl(path);

            const data = {
                ...baseParams,
                audit_referer_path: window.location.href,
                access_token: accessToken
            };
            if (!shouldRemoveLocale && typeof locale === 'string') {
                data._locale = locale;
            }

            const headers = {};
            if (withAuthHeader && accessToken) {
                headers.Authorization = `Bearer ${accessToken}`;
            }

            const fetchParams = {
                method: queryMethod,
                mode: 'cors',
                credentials: useCredentials ? 'include' : 'omit',
                headers
            };

            if (queryMethod === 'POST') {
                if (asJson) {
                    headers['Content-Type'] = 'application/json';
                    fetchParams.body = JSON.stringify(data);
                } else {
                    fetchParams.body = formData(data);
                }
            } else {
                const qs = lct.stringify(data);
                localUrl = localUrl.includes('?') ? `${localUrl}&${qs}` : `${localUrl}?${qs}`;
            }

            return fetch(localUrl, fetchParams)
                .then(r => {
                    if (r.status >= 200 && r.status < 300) return r;
                    r.clone().text().then((t) => { 
                        try { 
                            console.error('API Error Response:', {
                                status: r.status,
                                statusText: r.statusText,
                                url: localUrl,
                                body: t
                            }); 
                        } catch (e) {} 
                    });
                    const e = new Error(r.statusText); e.response = r; throw e;
                })
                .then(async (r) => {
                    const ct = (r.headers && r.headers.get && r.headers.get('content-type')) || '';
                    const text = await r.text();
                    if (ct.indexOf('application/json') !== -1 || /^\s*[\[{]/.test(text)) {
                        try { return JSON.parse(text); } catch { return { response: text }; }
                    }
                    return { response: text };
                })
                .catch(err => { throw err; });
        };

        const promise = new Promise((resolve, reject) => {
            tryFetch(true)
                .then((resp) => {
                    if (!resp.error) {
                        // ok
                    } else if (resp.error.code === 401) {
                        if (SessionActions.isLoopToken()) {
                            push(`${config.defaultRoute}401`, {
                                replace: true,
                                state: { message: resp.error.message },
                            });
                        } else {
                            SessionActions.getAccessToken();
                        }
                    } else {
                        reject(resp);
                        return;
                    }
                    setTimeout(() => resolve(resp), 0);
                })
                .catch((err) => {
                    if (err && err.response && err.response.status === 405) {
                        try {
                            const isGetLike = /\.get(?:$|[?#])/i.test(rawPath);
                            if (queryMethod === 'POST' && isGetLike) {
                                const headers = {};
                                if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
                                let localUrl = formUrl(path);
                                const data = {
                                    ...(typeof baseParams === 'string' ? { fields: baseParams } : baseParams),
                                    audit_referer_path: (window && window.location && window.location.href) ? window.location.href : '/',
                                    access_token: accessToken
                                };
                                if (!shouldRemoveLocale && typeof locale === 'string') data._locale = locale;
                                const qs = lct.stringify(data);
                                localUrl = localUrl.includes('?') ? `${localUrl}&${qs}` : `${localUrl}?${qs}`;
                                fetch(localUrl, { method: 'GET', mode: 'cors', credentials: useCredentials ? 'include' : 'omit', headers })
                                    .then(r => {
                                        if (r.status >= 200 && r.status < 300) return r;
                                        const e = new Error(r.statusText); e.response = r; throw e;
                                    })
                                    .then(async (r) => {
                                        const ct = (r.headers && r.headers.get && r.headers.get('content-type')) || '';
                                        const text = await r.text();
                                        if (ct.indexOf('application/json') !== -1 || /^\s*[\[{]/.test(text)) {
                                            try { return JSON.parse(text); } catch { return { response: text }; }
                                        }
                                        return { response: text };
                                    })
                                    .then((resp) => setTimeout(() => resolve(resp), 0))
                                    .catch((e2) => {
                                        const r = e2 && e2.response ? e2.response : err.response;
                                        r.text().then((t) => {
                                            let details = t; let parsed;
                                            try { parsed = JSON.parse(t); } catch {}
                                            const payload = parsed || details;
                                            setTimeout(() => resolve({ error: { code: r.status || 0, message: r.statusText || e2.message, payload } }), 0);
                                        }).catch(() => setTimeout(() => resolve({ error: { code: 0, message: e2 && e2.message ? e2.message : 'Request failed' } }), 0));
                                    });
                                return;
                            }
                        } catch (_) {  }
                    }
                    const isNetworkError = err && err.name === 'TypeError';
                    if (isNetworkError) {
                        tryFetch(false)
                            .then((resp) => setTimeout(() => resolve(resp), 0))
                            .catch(reject);
                        return;
                    }

                    if (err && err.response) {
                        err.response.text().then((t) => {
                            let details = t; let parsed;
                            try { parsed = JSON.parse(t); } catch {}
                            const payload = parsed || details;
                            
                            if (err.response.status === 401 || err.response.status === 403) {
                                console.warn('Access Denied Error:', {
                                    status: err.response.status,
                                    statusText: err.response.statusText,
                                    url: localUrl,
                                    payload: payload,
                                    message: typeof payload === 'string' ? payload : payload?.message || 'Access denied'
                                });
                            } else {
                                console.error('API Error Details:', {
                                    status: err.response.status,
                                    statusText: err.response.statusText,
                                    url: localUrl,
                                    payload: payload
                                });
                            }
                            
                            setTimeout(() => resolve({ error: { code: err.response.status, message: err.response.statusText, payload } }), 0);
                        }).catch(() => {
                            console.error('API Error (no response body):', err.message);
                            setTimeout(() => resolve({ error: { code: 0, message: err.message } }), 0);
                        });
                        return;
                    }

                    setTimeout(() => resolve({ error: { code: 0, message: err && err.message ? err.message : 'Request failed' } }), 0);
                });
        });

        PaceProgressActions.addRequest(promise);
        return promise;
    }
};